import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStorage } from '~Components/Storage';
import { useAdyen, useBridgeApi, useDispatch, useLicenses, useLogger, useSelector } from '~Hooks';

import * as actions from '~actions';
import { PutEventsInputEvent } from '~Components/EventsSynchronizer';
import moment from 'moment';

interface StartPaymentMessage {
  type: 'START_PAYMENT_REQUEST';
  terminalId: string;
  serviceId: string;
  amount: number;
  transactionId: string;
}

export interface SocketAPI {
  socketState: SOCKET_STATE;
  managedTerminals: Set<string>;
  send: {
    assignTerminal: (id: string) => void;
    selfIdentify: (id: string) => void;
    tableEvent: (event: PutEventsInputEvent) => void;
    orderStatusEvent: (event: PutEventsInputEvent) => void;
    adyenRequestResponse: (response: any) => void;
    registerManagedTerminal: (id: string) => void;
    sendToTerminal: (terminalId: string, message: StartPaymentMessage) => void;
  };
}

export enum SOCKET_STATE {
  CONNECTING = 'CONNECTING',
  OPEN = 'OPEN',
  CLOSING = 'CLOSING',
  CLOSED = 'CLOSED',
  ERROR = 'ERROR',
}

type Listener = (ev: Event) => any;

const Context = React.createContext<SocketAPI>({
  socketState: SOCKET_STATE.CONNECTING,
  managedTerminals: new Set(),
  send: {
    assignTerminal: () => {},
    tableEvent: () => {},
    orderStatusEvent: () => {},
    selfIdentify: () => {},
    adyenRequestResponse: () => {},
    registerManagedTerminal: () => {},
    sendToTerminal: () => {},
  },
});

const URL = process.env.ADYEN_SOCKET_URL!;

const createSocket = () => new WebSocket(URL);

const initialSocket = createSocket();

type SocketAction =
  | 'ping'
  | 'assign-terminal'
  | 'self-assign-terminal'
  | 'request-response'
  | 'put-table-event'
  | 'put-order-status-event'
  | 'broadcast-venue-tables'
  | 'broadcast-venue-statuses'
  | 'register-managed-terminal'
  | 'send-to-terminal';

const SocketProvider: React.FunctionComponent = ({ children }) => {
  const socket = useRef(initialSocket);
  const timestampofLastPong = useRef(0);
  const logger = useLogger('socket-manager');
  const dispatch = useDispatch();
  const venueId = useSelector((state) => state.venueId);
  const licenses = useLicenses();
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [socketState, setSocketState] = useState<SOCKET_STATE>(SOCKET_STATE.CONNECTING);
  const [storage, updateStorage] = useStorage();
  const adyen = useAdyen();
  const bridgeApi = useBridgeApi();
  const [requestToAdd, setRequestToAdd] = useState<any>();
  const [managedTerminals, setManagedTerminals] = useState<Set<string>>(new Set());

  const pingTimestamp = useRef(0);

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: SocketProvider');
    return () => {
      mountLogger.info('Unmounted: SocketProvider');
    };
  }, []);

  // Timeout that fires if no pong arrives within 5 seconds of a ping
  const pingPongTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  /* ==================================
      SEND HANDLERS
     ================================== */

  function sendMessage(action: SocketAction, data?: object) {
    try {
      socket.current.send(
        JSON.stringify({
          action,
          data,
        }),
      );
    } catch (error) {
      logger.error('Error trying to send web socket message', {
        errorMessage: error,
        action,
        data,
      });
    }
  }

  /**
   * Sends a terminal ID to be backend, indicating that this device
   * should be responsible for it's updates
   */
  const assignTerminal = useCallback((id: string) => {
    sendMessage('assign-terminal', { id });
  }, []);

  /**
   * Sends a table event to be backend
   */
  const tableEvent = useCallback((event: PutEventsInputEvent) => {
    sendMessage('put-table-event', event);
  }, []);

  /**
   * Sends an order status event to be backend
   */
  const orderStatusEvent = useCallback((event: PutEventsInputEvent) => {
    sendMessage('put-order-status-event', event);
  }, []);

  /**
   * Sends a self-identify message to the backend
   */
  const selfIdentify = useCallback((id: string) => {
    sendMessage('self-assign-terminal', { id });
  }, []);

  /**
   * Sends a response to an adyen request
   */
  const adyenRequestResponse = useCallback((response: any) => {
    sendMessage('request-response', response);
  }, []);

  /**
   * Registers this device as a managed terminal.
   * Used by the ManagedPayments scene to identify itself.
   */
  const registerManagedTerminal = useCallback((id: string) => {
    sendMessage('register-managed-terminal', { id });
  }, []);

  /**
   * Sends a message to a managed terminal (e.g. start payment).
   * Used by the POS to initiate payments on remote terminals.
   */
  const sendToTerminal = useCallback((terminalId: string, message: StartPaymentMessage) => {
    sendMessage('send-to-terminal', { terminalId, message });
  }, []);

  /**
   * Sends a venue ID to be backend, indicating that this device
   * should be responsible for its updates for the respective services
   */
  function broadcastVenue() {
    // Let the table service know we want table events
    if (licenses.hasTSR || licenses.atleastOneDeviceHasTSR) {
      sendMessage('broadcast-venue-tables', { venueId });
    }

    // Let the order status service know we want status events
    if (licenses.hasKDS || licenses.atleastOneDeviceHasKDS) {
      sendMessage('broadcast-venue-statuses', { venueId });
    }
  }

  function secondsSinceLastPingPong() {
    return (new Date().getTime() - timestampofLastPong.current) / 1000;
  }

  /* ==================================
      LIFECYCLE FUNCTIONS
     ================================== */

  const closeListener = useCallback((event: CloseEvent) => {
    setSocketState(SOCKET_STATE.CLOSED);

    logger.info(`Socket closed. Code: ${event.code}`);

    // Reconnect with a tiny timeout, so we dont blast our backend
    // in case something goes wrong
    setTimeout(reconnect, 1000);
  }, []);

  const openListener = useCallback(() => {
    /**
     * Ping the socket once on boot to ensure theres a connection.
     * Stamp the time first so the first pong reports a real latency
     */
    pingTimestamp.current = new Date().getTime();
    sendMessage('ping');

    setSocketState(SOCKET_STATE.OPEN);
  }, []);

  const errorListener = useCallback(() => {
    logger.error('Socket error, closing...');

    dispatch(actions.socketConnectionLost());

    socket.current.close();
  }, []);

  const messageListener = useCallback((event: MessageEvent) => {
    try {
      // If this is our keep-alive pingpong, ignore
      if (event.data === 'pong') {
        timestampofLastPong.current = new Date().getTime();

        const diff = moment().diff(pingTimestamp.current, 'ms');
        dispatch(actions.socketPongReceived(diff));

        if (diff > 3000) {
          logger.warn('Pong received within ' + diff + ' ms');
        }
        return;
      }

      const message = JSON.parse(event.data);
      const { message: errorMessage, domain, payload } = message;

      if (errorMessage) {
        logger.error(errorMessage, {
          message,
        });
        return;
      }

      if (!domain) {
        logger.error('Missing domain in socket message', {
          message,
        });
      }

      switch (domain) {
        case 'ADYEN_EVENT':
          if (!payload.SaleToPOIRequest?.MessageHeader) {
            logger.warn('Invalid adyen event', {
              message,
            });

            return;
          }

          window.postMessage({ type: 'ADYEN_EVENT', message: payload }, '*');
          break;

        case 'ADYEN_REQUEST':
          logger.info('TSR: New Adyen Request', {
            request: payload,
          });

          dispatch({
            type: 'ADD_ADYEN_REQUEST',
            request: payload,
          });

          break;
        case 'TABLE_EVENT':
          dispatch(actions.addTableEvent(payload));
          break;
        case 'ORDER_STATUS_EVENT':
          dispatch(actions.addOrderStatusEvent(payload));
          break;
        case 'MANAGED_TERMINAL_STATUS':
          logger.info('Managed terminal status update', { payload });
          setManagedTerminals((prev) => {
            const next = new Set(prev);
            if (payload.connected) {
              next.add(payload.terminalId);
            } else {
              next.delete(payload.terminalId);
            }
            return next;
          });
          break;
        case 'START_PAYMENT_REQUEST':
          logger.info('Received start payment request', { payload });
          dispatch({
            type: 'START_PAYMENT_REQUEST',
            payload,
          });
          break;
        case 'EVENT_CONFIRMED':
          // Do nothing for now
          break;
        default:
          logger.warn('Unknown socket event domain', {
            message,
          });
      }
    } catch (e) {
      if (e instanceof Error) {
        logger.warn(e.message, { event });
      }
    }
  }, []);

  useEffect(() => {
    reconnect();
  }, []);

  function reconnect() {
    //  Clear all event listeners from previous connection
    socket.current.removeEventListener('close', closeListener);
    socket.current.removeEventListener('open', openListener);
    socket.current.removeEventListener('message', messageListener);
    socket.current.removeEventListener('error', errorListener);

    // Create a new socket instance
    socket.current = createSocket();

    setSocketState(SOCKET_STATE.CONNECTING);

    // Set up event listeners
    socket.current.addEventListener('open', openListener);
    socket.current.addEventListener('close', closeListener);
    socket.current.addEventListener('error', errorListener);
    socket.current.addEventListener('message', messageListener);

    // And trigger a re-run of the event listener setup effect
    setRetryAttempt((r) => r + 1);
  }

  /* ==================================
      SOCKET STATE HANDLING
     ================================== */

  useEffect(() => {
    /* ==================================
      EFFECTS AND MORE LIFECYCLE
     ================================== */

    /**
     * For some reason, socket connections for adyen terminals
     * report 410 GONE exceptions in backend. So here we try
     * to simply resend the connected terminals every 10 minutes
     * to ensure the backend stays aware of this client.
     */
    const terminalResendInterval = setInterval(() => {
      storage.adyenTerminals.forEach((terminal) => {
        assignTerminal(terminal.POIID);
      });
    }, 1000 * 60 * 10);

    /**
     * Ping the socket with regular intervals to keep it alive
     * API Gateway timeout is 10 minutes, but 1 minute feels... better
     */
    const id = setInterval(() => {
      sendMessage('ping');
      pingTimestamp.current = new Date().getTime();

      // Ensure we get a pong back within 5 seconds
      pingPongTimeoutRef.current = setTimeout(() => {
        const diff = moment().diff(timestampofLastPong.current, 'seconds');

        if (diff > 5) {
          logger.warn('No pong received within 5 seconds');
          logger.warn('Restarting socket connection...');

          dispatch(actions.socketConnectionLost());

          // Not sure if this try / catch is needed but lets stay safe
          try {
            socket.current.close();
          } catch (e) {
            logger.error((e as Error).message);
          }

          reconnect();
        }
      }, 5000);
    }, 60000);

    // Refresh the connection every 90 minutes.
    // AWS API Gateway session timeout is 2 hours but lets have some margin
    const refreshId = setTimeout(() => {
      logger.info('Refreshing socket connection...');

      const diff = secondsSinceLastPingPong();
      logger.info('Seconds since last pingpong: ' + diff);

      socket.current.close();
    }, 60 * 90 * 1000);

    return () => {
      clearTimeout(refreshId);
      clearInterval(id);
      clearInterval(terminalResendInterval);
      clearTimeout(pingPongTimeoutRef.current);
    };
  }, [retryAttempt]);

  useEffect(() => {
    if (socketState !== SOCKET_STATE.OPEN) {
      return;
    }

    /**
     * Send all terminals configured for this device to the backend.
     * This will route all P@T requests for that terminal to this device
     */
    storage.adyenTerminals.forEach((terminal) => {
      assignTerminal(terminal.POIID);
    });
  }, [socketState, storage.adyenTerminals]);

  // Always have the socket state as context to all logs
  useEffect(() => {
    logger.setContext({
      socketState,
    });

    logger.info(`Socket state: ${socketState}`);
  }, [socketState]);

  useEffect(() => {
    if (!venueId || socketState !== SOCKET_STATE.OPEN) {
      return;
    }

    // broadcast our connection
    // to start accepting new events
    broadcastVenue();
  }, [licenses.hasTSR, licenses.atleastOneDeviceHasKDS, licenses.hasKDS, socketState, venueId]);

  /**
   * For unknown reasons, sockets sometimes get stuck
   * in a "closing" state. This effect will close them
   * forcefully after 5 seconds. (Ready State 2)
   */
  useEffect(() => {
    if (socketState !== SOCKET_STATE.CLOSING) {
      return;
    }

    const id = setTimeout(() => {
      logger.warn('Socket stuck in closing state, closing forcefully');
      reconnect();
    }, 5000);

    return () => {
      clearTimeout(id);
    };
  }, [socketState]);

  const api: SocketAPI = useMemo(
    () => ({
      socketState,
      managedTerminals,
      send: {
        assignTerminal,
        tableEvent,
        orderStatusEvent,
        selfIdentify,
        adyenRequestResponse,
        registerManagedTerminal,
        sendToTerminal,
      },
    }),
    [socketState, managedTerminals],
  );

  return <Context.Provider value={api}>{children}</Context.Provider>;
};

export const useSocket = () => {
  return useContext(Context);
};

export default SocketProvider;
